import type { BarProgress, CoachConfig } from "../engine/coach-params";
import type { Passage } from "../engine/coach-state";

// Per-song Coach practice context, persisted to localStorage so a session resumes
// where it left off — per-bar mastery PLUS the live passage layout (tempo ramp /
// phase / window / active passage), so the tempo doesn't drop back to the floor on
// re-attach. Pure storage + a staleness-decay policy (light spaced repetition); the
// coach engine stays unaware of persistence.

/** The live passage layout to resume (tempo/phase/window/active). */
interface Runtime {
  passages: Passage[];
  active: number;
}

interface SavedProgress {
  bars: BarProgress[];
  /** Live layout for resume (absent on pre-C5 saves → recompute from mastery). */
  passages?: Passage[];
  active?: number;
  /** Epoch ms when saved (for staleness decay). */
  savedAt: number;
}

const key = (songId: string): string => `drop-notes.coach-progress:${songId}`;
const DAY_MS = 86_400_000;

export function saveCoachProgress(
  songId: string,
  bars: BarProgress[],
  nowMs: number,
  runtime?: Runtime,
): void {
  if (!songId) return;
  try {
    const payload: SavedProgress = {
      bars,
      savedAt: nowMs,
      ...(runtime ? { passages: runtime.passages, active: runtime.active } : {}),
    };
    localStorage.setItem(key(songId), JSON.stringify(payload));
  } catch {
    // ignore — progress simply won't persist
  }
}

/**
 * Load saved progress for a song, applying staleness decay. Returns null when
 * there is none, it is malformed, or the bar count no longer matches the song
 * (so a re-parsed/edited file never resumes onto a mismatched grid).
 */
function parseBars(raw: SavedProgress["bars"]): BarProgress[] {
  return raw.map((b) => {
    const bar: BarProgress = {
      masteryReps: Math.max(0, Number(b?.masteryReps) || 0),
      mastered: Boolean(b?.mastered),
    };
    if (Number.isFinite(b?.lastPracticedMs)) bar.lastPracticedMs = Number(b?.lastPracticedMs);
    return bar;
  });
}

export function loadCoachProgress(
  songId: string,
  barCount: number,
  cfg: CoachConfig,
  nowMs: number,
): BarProgress[] | null {
  if (!songId) return null;
  try {
    const raw = localStorage.getItem(key(songId));
    if (!raw) return null;
    const saved = JSON.parse(raw) as SavedProgress;
    if (!Array.isArray(saved.bars) || saved.bars.length !== barCount) return null;
    return decay(parseBars(saved.bars), Number(saved.savedAt) || 0, cfg, nowMs);
  } catch {
    return null;
  }
}

/**
 * Load the saved live layout (tempo/phase/window/active) to resume the tempo ramp.
 * Returns null when absent, malformed, the bar count changed, OR staleness decay
 * would alter mastery — in that last case the saved tempo/window may no longer match
 * the (decayed) bars, so the coach recomputes the layout from mastery instead. Pair
 * with {@link loadCoachProgress} (which returns the decayed bars).
 */
export function loadCoachRuntime(
  songId: string,
  barCount: number,
  cfg: CoachConfig,
  nowMs: number,
): Runtime | null {
  if (!songId) return null;
  try {
    const raw = localStorage.getItem(key(songId));
    if (!raw) return null;
    const saved = JSON.parse(raw) as SavedProgress;
    if (!Array.isArray(saved.bars) || saved.bars.length !== barCount) return null;
    if (!Array.isArray(saved.passages) || saved.passages.length === 0) return null;
    // A singleChain session produces exactly one passage; restoring a multi-passage
    // runtime would defeat the toggle by keeping the old chain count. Recompute instead.
    if (cfg.singleChain && saved.passages.length > 1) return null;
    const before = parseBars(saved.bars);
    const after = decay(before, Number(saved.savedAt) || 0, cfg, nowMs);
    const decayed = after.some(
      (b, i) => b.masteryReps !== before[i]?.masteryReps || b.mastered !== before[i]?.mastered,
    );
    if (decayed) return null; // mastery moved → don't trust the saved tempo/window
    return { passages: saved.passages, active: Math.max(0, Number(saved.active) || 0) };
  } catch {
    return null;
  }
}

/** Ids of songs whose saved Coach progress is fully mastered (every bar). Used
 *  by the curriculum sequencer; reads the raw saved flags (no song re-parse). */
export function masteredSongIds(): Set<string> {
  const prefix = "drop-notes.coach-progress:";
  const ids = new Set<string>();
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(prefix)) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const saved = JSON.parse(raw) as { bars?: { mastered?: boolean }[] };
      if (
        Array.isArray(saved.bars) &&
        saved.bars.length > 0 &&
        saved.bars.every((b) => b?.mastered === true)
      ) {
        ids.add(k.slice(prefix.length));
      }
    }
  } catch {
    // ignore corrupt/blocked storage
  }
  return ids;
}

/** The fully-mastered song with the most room to firm up: lowest average
 *  `masteryReps` across its bars, ties broken by oldest save (spaced-repetition
 *  friendly). Null when no song is fully mastered. The continuous coach loop uses
 *  this to pick a replay once the curriculum has no new suggestion. */
export function weakestMasteredId(): string | null {
  const prefix = "drop-notes.coach-progress:";
  let best: { id: string; avg: number; savedAt: number } | null = null;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(prefix)) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const saved = JSON.parse(raw) as {
        bars?: { mastered?: boolean; masteryReps?: number }[];
        savedAt?: number;
      };
      const bars = saved.bars;
      if (!Array.isArray(bars) || bars.length === 0) continue;
      if (!bars.every((b) => b?.mastered === true)) continue;
      const total = bars.reduce((sum, b) => sum + (Number(b?.masteryReps) || 0), 0);
      const avg = total / bars.length;
      const savedAt = Number(saved.savedAt) || 0;
      if (!best || avg < best.avg || (avg === best.avg && savedAt < best.savedAt)) {
        best = { id: k.slice(prefix.length), avg, savedAt };
      }
    }
  } catch {
    // ignore corrupt/blocked storage
  }
  return best?.id ?? null;
}

export function clearCoachProgress(songId: string): void {
  try {
    localStorage.removeItem(key(songId));
  } catch {
    // ignore
  }
}

/** Per-bar light spaced repetition: each mastered bar older than `staleDays`
 *  (by its own `lastPracticedMs`, falling back to the session `savedAt` for
 *  pre-C4 saves) loses `decayReps`. Differential — a bar you firmed up yesterday
 *  survives while one you haven't touched in a week decays. */
function decay(
  bars: BarProgress[],
  savedAt: number,
  cfg: CoachConfig,
  nowMs: number,
): BarProgress[] {
  if (cfg.decayReps <= 0) return bars;
  return bars.map((b) => {
    if (!b.mastered) return b;
    const practicedAt = b.lastPracticedMs ?? savedAt;
    if ((nowMs - practicedAt) / DAY_MS < cfg.staleDays) return b;
    const masteryReps = Math.max(0, b.masteryReps - cfg.decayReps);
    return { ...b, masteryReps, mastered: masteryReps >= cfg.masteryReps };
  });
}
